import { CheckTransaction } from '@/api/geovera/specialty-check/check-transactions';
import { Button } from '@/components/ui/button';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowUpDown, Eye } from 'lucide-react';

import { CheckTransactionDetails } from './details';

export const columns: ColumnDef<CheckTransaction>[] = [
	{
		accessorKey: 'policyNumber',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Policy #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">
				{row.original.policyNumber}
			</div>
		),
	},
	{
		accessorKey: 'checkNumber',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Check #
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">
				{row.original.checkNumber}
			</div>
		),
	},
	{
		accessorKey: 'invoiceDate',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Invoice Date
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => {
			return (
				<div className="font-medium">
					{row.original.invoiceDate ? format(row.original.invoiceDate, 'P') : '---'}
				</div>
			);
		},
	},
	{
		accessorKey: 'insuredName1',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Insured Name
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">
				{row.original.insuredName1}
			</div>
		),
	},
	{
		accessorKey: 'insuredName2',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Insured Name 2
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="font-medium">{row.original.insuredName2}</div>
		),
	},
	{
		accessorKey: 'street',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Street
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">{row.original.street}</div>
		),
	},
	{
		accessorKey: 'city',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				City
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => <div className="font-medium">{row.original.city}</div>,
	},
	{
		accessorKey: 'state',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				State
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => <div className="font-medium">{row.original.state}</div>,
	},
	{
		accessorKey: 'zip',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Zip
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => <div className="font-medium">{row.original.zip}</div>,
	},
	{
		accessorKey: 'checkAmount',
		header: ({ column }) => (
			<div className="text-right">
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					Amount
					<ArrowUpDown />
				</Button>
			</div>
		),
		cell: ({ row }) => {
			let formattedAmount;
			if (row.original.checkAmount) {
				formattedAmount = new Intl.NumberFormat('en-US', {
					style: 'currency',
					currency: 'USD',
				}).format(row.original.checkAmount);
			}
			return <div className="text-right font-medium">{formattedAmount}</div>;
		},
	},
	{
		accessorKey: 'reason',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				Reason
				<ArrowUpDown />
			</Button>
		),
		cell: ({ row }) => (
			<div className="whitespace-nowrap font-medium">{row.original.reason}</div>
		),
	},
	{
		accessorKey: 'isUploaded',
		header: () => <div className="whitespace-nowrap">Is Uploaded</div>,
		cell: ({ row }) => (
			<div className="font-medium">{row.original.isUploaded ? 'Yes' : 'No'}</div>
		),
	},
	{
		id: 'details',
		header: () => <div className="ml-2">Details</div>,
		cell: ({ row }) => (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						<CheckTransactionDetails transaction={row.original} />
					</TooltipTrigger>
					<TooltipContent>
						<p>
							{!row.original.error
								? 'No error'
								: `View error for ${row.original.policyNumber}`}
						</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		),
		enableSorting: false,
	},
];
